import React, { useEffect, useState, useMemo, useContext } from "react";
import { Tabs, Tooltip } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router";
import { FileExcelOutlined } from "@ant-design/icons";

import AboutCompany from "./AboutCompany";
import KeyExecutives from "./KeyExecutives";
import OrgChart from "./OrgChart";
import { getUserInfo } from "../../utils/utils";
import { errEnum, subscriptionContentInfo } from "../../config";
import TrialModal from "../../common/TrialModal";
import popupImg from "../../assets/images/free-user-login-prompt.jpg.jpeg";
import subscribepopupImg from "../../assets/images/subscribe-now-prompt-img.jpg";
import {
  getEmployeeList,
  resetEmployeeList,
  getRelavantCompany,
  resetPostRelavantCompany,
  storeSelectedDepartment,
  emptyErrorObj,
  getCompanyTag,
  resetCompanyTag,
  downloadExecutiveExl,
} from "../../actionCreator/companyDetailsActionCreator";
import { saveExcel } from "../../utils";
import { useExport } from "../executive-search/hooks/useExport";
import TagExecutive from "../executive-search/TagExecutive";
import { useAlert } from "../../context/AlertProvider";
import { AuthContext } from "../../context/AuthContext";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import tag from "../../assets/svg/tag.svg";

const SummaryContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { id } = useParams();
  const { isAuthenticated: isLoggedIn } = useContext(AuthContext);
  const { getSchema, columns } = useExport();
  const [showModal, setShowModal] = useState(false);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });
  const [dropDownToggle, setDropdownToggle] = useState(false);
  const [tabActiveKey, setTabActiveKey] = useState("1");
  const [similarCount, setSimilarCount] = useState(5);
  const [selectedValue, setSelectedValue] = useState("Filter by Department");
  const [departmentAllList, setDepartmentAllList] = useState();
  const [openTagModal, setOpenTagModal] = useState(false);

  const [isApiFailed, setIsApiFailed] = useState(false);
  const departmentList = useSelector(
    (state) => state.companyDetailsReducer.departmentList,
  );
  const companyDetails = useSelector(
    (state) => state.companyDetailsReducer.companyDetails,
  );
  const similarCompanyList = useSelector(
    (state) => state.companyDetailsReducer.similarCompanyList,
  );

  const userAccountInfo = useSelector(
    (state) => state.CommonReducer.accountInfo,
  );

  const selectedEmployeeList = useSelector(
    (state) => state.companyDetailsReducer.selectedExecutive,
  );

  const selectedDepartment = useSelector(
    (state) => state.companyDetailsReducer.selectedDepartment,
  );

  const errObj = useSelector((state) => state.companyDetailsReducer.errObj);

  useEffect(() => {
    setIsApiFailed(!!Object.keys(errObj).length);
  }, [Object.keys(errObj).length]);

  useMemo(() => {
    if (Object.keys(getUserInfo()).length && companyDetails?.id) {
      const { id } = getUserInfo();
      dispatch(resetCompanyTag());
      dispatch(getCompanyTag(companyDetails?.id, id));
    }
  }, [companyDetails, userAccountInfo]);

  useMemo(() => {
    dispatch(resetPostRelavantCompany);
    if (Object.keys(getUserInfo()).length && companyDetails?.id) {
      const { id } = getUserInfo();
      dispatch(getRelavantCompany(id, companyDetails?.id));
    }
  }, [userAccountInfo]);

  useEffect(() => {
    setSimilarCount(
      similarCompanyList.length < 5 ? similarCompanyList.length : 5,
    );
  }, [similarCompanyList]);

  useEffect(() => {
    const dpt = [{ id: 0, name: "All" }, ...departmentList];
    setDepartmentAllList(dpt);
  }, [departmentList]);

  const items = [
    {
      key: "1",
      label: "About Company",

      children: <AboutCompany />,
    },
    {
      key: "2",
      label: "Key Executives",
      children: <KeyExecutives />,
    },
    {
      key: "3",
      label: "Org. Chart",
      children: <OrgChart />,
    },
    {
      key: "4",
      label: isLoggedIn ? (
        <FeatureComingSoon>Triggers</FeatureComingSoon>
      ) : (
        "Triggers"
      ),
      children: <></>,
    },
  ];

  const onChange = (key) => {
    if (!isLoggedIn && (key === "3" || key === "4")) {
      setShowModal(false);
      setOpenModal({ info: null, open: true });
    } else {
      setTabActiveKey(key);
      if (key !== "2" && key !== "3") {
        setSelectedValue("Filter by Department");
        dispatch(getEmployeeList(id));
      }
    }
  };

  const toggleDropdown = () => {
    setDropdownToggle(!dropDownToggle);
  };

  const setValue = (selectedItem) => {
    dispatch(resetEmployeeList());
    dispatch(getEmployeeList(id, selectedItem?.id));
    dispatch(storeSelectedDepartment(selectedItem?.id));
    setSelectedValue(selectedItem?.name);
    setDropdownToggle(!dropDownToggle);
    if (tabActiveKey === "3") {
      setTabActiveKey("3");
    } else {
      setTabActiveKey("2");
    }
  };

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setOpenModal(false);
    dispatch(emptyErrorObj());
  };

  const redirectToSignup = () => {
    setShowModal(false);
    navigate("/signup");
  };

  const tagExecutive = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      if (selectedEmployeeList?.length) {
        setOpenTagModal(true);
      } else {
        showAlert("warning", "please select record.");
      }
    }
  };

  const downloadExcel = async () => {
    checkLoginStatus();
    if (isLoggedIn) {
      let selectedEmpIds = "";
      selectedEmployeeList?.forEach((exe) => {
        selectedEmpIds += `${exe?.id},`;
      });
      selectedEmpIds = selectedEmpIds.substring(
        selectedEmpIds.lastIndexOf(","),
        0,
      );
      const payload = {
        empIds: selectedEmpIds,
        department: selectedDepartment,
        companyId: companyDetails?.id,
      };
      const res = await dispatch(downloadExecutiveExl(payload));
      if (res?.length) {
        const downloadedUpdatedData = getSchema(res);
        const fileName = companyDetails.name + "-executive-data";
        saveExcel(downloadedUpdatedData, columns, fileName);
        showAlert(
          "success",
          "Download Complete",
          <div>
            The key executive data for the {companyDetails.name} has been
            successfully downloaded to{" "}
            <FileExcelOutlined style={{ color: "#4CAF50" }} />.
          </div>,
        );
      }
    }
  };

  const closeTagModal = () => {
    setOpenTagModal(false);
  };

  return (
    <>
      <div className="row mx-0">
        <div className="col-md-12">
          <div className="company-details-filter d-flex align-items-center justify-content-end gap-20 mt30">
            <div className="selectedlabel fa" onClick={toggleDropdown}>
              <i className="text-black fa fa-suitcase pr-2"></i>
              <span>{selectedValue}</span>
            </div>
            <ul className={`departmentOptions ${dropDownToggle ? "show" : ""}`}>
              {departmentAllList?.map((item) => {
                return (
                  <li key={item?.id} onClick={() => setValue(item)}>
                    {item?.name}
                  </li>
                );
              })}
            </ul>
            <div className="d-flex align-items-center gap-10">
              <Tooltip title="Tag Executive">
                <img
                  src={tag}
                  alt=""
                  className="w-20 h-20 cursor-pointer"
                  onClick={tagExecutive}
                />
              </Tooltip>
              <Tooltip title="Download Excel">
                <i
                  className="right-icons la la-file-excel"
                  aria-hidden="true"
                  onClick={() => downloadExcel()}
                ></i>
              </Tooltip>
            </div>
          </div>

          <div className="mt-4 mb-4 summarycontainer">
            <Tabs activeKey={tabActiveKey} items={items} onChange={onChange} />
          </div>
        </div>
      </div>

      <TrialModal
        openModal={showModal}
        closeModal={closeModal}
        redirectToSignup={redirectToSignup}
        redirect={true}
        buttonText="Start Free Trial"
        modalBody={
          <div id="small-dialog2">
            <div align="center">
              <img src={popupImg} alt="" />
            </div>
            <p style={{ color: "#0000FF" }}>
              Get 10 free verified contacts with a BeyondLeadz Pro trial
            </p>
            <p>
              BeyondLeadz Pro customers close deals faster thanks to relevant
            </p>
          </div>
        }
        modalWidth="400px"
      />

      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
          buttonText={subscriptionContentInfo.btntext}
          modalBody={
            <div id="small-dialog2">
              <div align="center">
                <img src={subscribepopupImg} alt="" />
              </div>
              <p style={{ color: "#0000FF" }}>
                {subscriptionContentInfo.content}
              </p>
            </div>
          }
          modalWidth="400px"
        />
      )}

      {isApiFailed ? (
        <TrialModal
          openModal={isApiFailed}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              {errObj[errEnum.DOWNLOAD_ERROR]
                ? errObj[errEnum.DOWNLOAD_ERROR].message
                : "Please call System support. You application having some issue."}
            </div>
          }
          modalWidth="400px"
        />
      ) : (
        ""
      )}

      <TagExecutive
        selectedRecords={selectedEmployeeList?.map((d) => d?.id)}
        showTagModal={openTagModal && !!selectedEmployeeList?.length}
        closeTagModal={closeTagModal}
      />
    </>
  );
};

export default SummaryContent;
